<script setup lang="ts">
import { Loader2 } from 'lucide-vue-next';
import { computed, ref } from 'vue';

import Button from '@/components/ui/button/Button.vue';
import { useAccessContextStore } from '@/stores/accessContextStore';
import { useStripeStore } from '@/stores/stripeStore';

type Props = {
    isTrial: boolean;
    isGracePeriod: boolean;
};

const props = defineProps<Props>();

const accessContextStore = useAccessContextStore();
const stripeStore = useStripeStore();
const isButtonLoading = ref(false);

const trialDaysRemaining = computed(() => {
    return accessContextStore.trialDaysRemaining ?? 0;
});

const gracePeriodDaysRemaining = computed(() => {
    return accessContextStore.gracePeriodDaysRemaining ?? 0;
});

const trialPrimaryLine = computed(() => {
    const days = trialDaysRemaining.value;
    const dayLabel = days === 1 ? 'day' : 'days';

    if (days === 0) {
        return 'Your free trial has ended.';
    }

    if (days > 3) {
        return `Loving it so far? Your trial has ${days} ${dayLabel} left.`;
    }

    return `Your free trial ends in ${days} ${dayLabel}.`;
});

const trialSecondaryLine = computed(() => {
    if (trialDaysRemaining.value > 3) {
        return 'Upgrade to Pro and keep building.';
    }

    return "You've seen what's possible — now unlock the full experience with Pro.";
});

const gracePrimaryLine = computed(() => {
    const days = gracePeriodDaysRemaining.value;
    const dayLabel = days === 1 ? 'day' : 'days';

    return `Your grace period will end in ${days} ${dayLabel}.`;
});

const graceSecondaryLine = computed(() => {
    return 'Pay now to keep full access.';
});

const bannerPrimaryLine = computed(() => {
    return props.isGracePeriod
        ? gracePrimaryLine.value
        : trialPrimaryLine.value;
});

const bannerSecondaryLine = computed(() => {
    return props.isGracePeriod
        ? graceSecondaryLine.value
        : trialSecondaryLine.value;
});

const handleButtonClick = async () => {
    if (isButtonLoading.value) return;
    isButtonLoading.value = true;
    try {
        await stripeStore.openUpgradeFlow();
    } finally {
        isButtonLoading.value = false;
    }
};
</script>

<template>
    <div
        class="flex flex-col p-3 rounded-xl"
        :class="
            isGracePeriod
                ? 'bg-destructive/5 border border-destructive'
                : 'bg-primary10 border border-primary30'
        "
    >
        <div class="flex gap-2 items-center justify-between">
            <div
                class="text-sm"
                :class="isGracePeriod ? 'text-destructive' : 'text-foreground'"
            >
                <p class="font-semibold">
                    {{ bannerPrimaryLine }}
                </p>
                <p>
                    {{ bannerSecondaryLine }}
                </p>
            </div>
            <Button
                size="sm"
                :variant="isGracePeriod ? 'destructive' : 'default'"
                :disabled="isButtonLoading"
                @click="handleButtonClick"
            >
                <Loader2
                    v-if="isButtonLoading"
                    class="animate-spin h-4 w-4 mr-2"
                />
                {{ isGracePeriod ? 'Pay now' : 'Upgrade' }}
            </Button>
        </div>
    </div>
</template>
